import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';

const CredentialsModal = ({ isOpen, onClose, credentials, memberType }) => {
  const [copied, setCopied] = useState({
    email: false,
    password: false,
    all: false
  });

  if (!isOpen || !credentials) return null;

  const { email, password, masterId, role } = credentials;

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [field]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyAll = async () => {
    const allText = `Email: ${email}\nPassword: ${password}\n\nLogin at: ${memberType === 'contractor' ? '/contractor-login' : '/corporate-login'}`;
    await copyToClipboard(allText, 'all');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl w-full max-w-md mx-4 border border-border">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-500 bg-opacity-20 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="#22c55e" />
            </div>
            <h3 className="text-xl font-semibold text-text-primary">
              Account Created!
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          {/* Warning */}
          <div className="bg-amber-900 bg-opacity-30 border border-amber-500 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="AlertTriangle" size={20} color="#f59e0b" className="mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-400 font-medium">Save these credentials</p>
                <p className="text-amber-300 text-sm mt-1">
                  This is the only time the password will be displayed. Make sure to share it securely with the new member.
                </p>
              </div>
            </div>
          </div>

          {/* Role badge */}
          {role && (
            <div className="flex items-center space-x-2">
              <span className="text-text-secondary text-sm">Role:</span>
              <span className="px-2 py-1 bg-orange-500 bg-opacity-20 text-orange-400 rounded text-sm capitalize">
                {role.replace('_', ' ')}
              </span>
            </div>
          )}

          {/* Credentials */}
          <div className="space-y-3">
            {/* Email */}
            <div className="bg-surface rounded-lg p-4">
              <label className="block text-text-secondary text-xs font-medium mb-1">
                Email
              </label>
              <div className="flex items-center justify-between">
                <span className="text-text-primary font-mono text-sm break-all">{email}</span>
                <button
                  onClick={() => copyToClipboard(email, 'email')}
                  className="ml-2 p-2 text-text-secondary hover:text-text-primary transition-colors flex-shrink-0"
                  title="Copy email"
                >
                  {copied.email ? (
                    <Icon name="Check" size={16} color="#22c55e" />
                  ) : (
                    <Icon name="Copy" size={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Password */}
            <div className="bg-border rounded-lg p-4">
              <label className="block text-text-secondary text-xs font-medium mb-1">
                Password
              </label>
              <div className="flex items-center justify-between">
                <span className="text-text-primary font-mono text-sm">{password}</span>
                <button
                  onClick={() => copyToClipboard(password, 'password')}
                  className="ml-2 p-2 text-text-secondary hover:text-text-primary transition-colors flex-shrink-0"
                  title="Copy password"
                >
                  {copied.password ? (
                    <Icon name="Check" size={16} color="#22c55e" />
                  ) : (
                    <Icon name="Copy" size={16} />
                  )}
                </button>
              </div>
            </div>

            {/* Master ID (if available) */}
            {masterId && (
              <div className="bg-border bg-opacity-50 rounded-lg p-4">
                <label className="block text-text-secondary text-xs font-medium mb-1">
                  Platform ID
                </label>
                <span className="text-text-secondary font-mono text-sm">{masterId}</span>
              </div>
            )}
          </div>

          {/* Login URL info */}
          <div className="bg-border rounded-lg p-4">
            <label className="block text-text-secondary text-xs font-medium mb-1">
              Login URL
            </label>
            <span className="text-orange-400 text-sm">
              {memberType === 'contractor' ? '/contractor-login' : '/corporate-login'}
            </span>
          </div>

          {/* Copy All Button */}
          <button
            onClick={copyAll}
            className="w-full px-4 py-3 bg-border border border-border rounded-lg text-text-primary font-medium hover:bg-surface-light transition-colors flex items-center justify-center"
          >
            {copied.all ? (
              <>
                <Icon name="Check" size={18} color="#22c55e" className="mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Icon name="Copy" size={18} className="mr-2" />
                Copy All Credentials
              </>
            )}
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg text-white font-medium hover:from-orange-600 hover:to-orange-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredentialsModal;
